/**
 * useMediaLibraryUrlState
 *
 * Syncs MediaLibrary navigation state with the URL hash so users can share links,
 * use the browser back/forward buttons, and deep-link to specific views.
 *
 * URL format (all params are namespaced with "zml-" to avoid collisions):
 *   #zml-view=browser&zml-source=workspace-library&zml-type=image&zml-id=<assetId>&zml-q=<search>
 *
 * Supported views in the URL:
 *   home, browser, explore, asset, analytics, selection
 *
 * Transient views (gltf-missing-files) are intentionally excluded because they rely
 * on upload-queue state that cannot be meaningfully reconstructed from a URL.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { MediaLibraryView, AssetSource } from '../types';
import { MediaType } from '../types';

const PREFIX = 'zml';

const VALID_VIEWS = new Set<MediaLibraryView>([
  'home', 'browser', 'explore', 'asset', 'analytics', 'selection',
]);

// ─── Encoding / Decoding ─────────────────────────────────────────────────────

export interface UrlNavState {
  view: MediaLibraryView;
  source: AssetSource;
  mediaType: MediaType | null;
  /** ID of the asset shown in the 'asset' or 'analytics' view */
  assetId: string | null;
  searchQuery: string;
}

function key(name: string) {
  return `${PREFIX}-${name}`;
}

function encodeUrlState(state: Partial<UrlNavState>): string {
  const params = new URLSearchParams();
  if (state.view) params.set(key('view'), state.view);
  if (state.source) params.set(key('source'), state.source);
  if (state.mediaType) params.set(key('type'), state.mediaType);
  if (state.assetId) params.set(key('id'), state.assetId);
  if (state.searchQuery) params.set(key('q'), state.searchQuery);
  return params.toString();
}

function decodeUrlState(hash: string): Partial<UrlNavState> {
  // Strip leading '#'
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(raw);

  const state: Partial<UrlNavState> = {};

  const view = params.get(key('view'));
  if (view && VALID_VIEWS.has(view as MediaLibraryView)) {
    state.view = view as MediaLibraryView;
  }

  const source = params.get(key('source'));
  if (source) state.source = source as AssetSource;

  const type = params.get(key('type'));
  if (type && Object.values(MediaType).includes(type as MediaType)) {
    state.mediaType = type as MediaType;
  }

  const id = params.get(key('id'));
  if (id) state.assetId = id;

  const q = params.get(key('q'));
  if (q) state.searchQuery = q;

  return state;
}

// ─── Merging with existing hash ──────────────────────────────────────────────
//
// The page hash may contain params unrelated to the media library.  We preserve
// those by reading all existing hash params and only overwriting the zml-* ones.

function mergeIntoHash(state: Partial<UrlNavState>): string {
  const existing = new URLSearchParams(
    window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
  );

  // Remove all existing zml-* params
  for (const k of [...existing.keys()]) {
    if (k.startsWith(`${PREFIX}-`)) existing.delete(k);
  }

  // Write the new zml-* params
  const encoded = new URLSearchParams(encodeUrlState(state));
  encoded.forEach((v, k) => existing.set(k, v));

  return existing.toString();
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseMediaLibraryUrlStateOptions {
  /** Called when the browser navigates back/forward to a stored MediaLibrary state. */
  onPopState: (state: Partial<UrlNavState>) => void;
}

export interface UseMediaLibraryUrlStateResult {
  /** Initial URL state read once on mount. Empty object if no zml-* params are present. */
  initialUrlState: Partial<UrlNavState>;
  /**
   * Call this whenever the MediaLibrary state changes.  The URL hash will be
   * updated, creating a browser-history entry for back/forward support.
   */
  pushUrlState: (state: Partial<UrlNavState>) => void;
}

export function useMediaLibraryUrlState(
  enabled: boolean,
  options: UseMediaLibraryUrlStateOptions
): UseMediaLibraryUrlStateResult {
  const { onPopState } = options;

  // Read the initial state from the URL hash exactly once on mount.
  const initialUrlState = useRef<Partial<UrlNavState>>(
    enabled ? decodeUrlState(window.location.hash) : {}
  ).current;

  // Track the last hash we wrote so we can ignore the resulting hashchange event.
  const lastWrittenHash = useRef<string | null>(null);

  const pushUrlState = useCallback(
    (state: Partial<UrlNavState>) => {
      if (!enabled) return;
      const newHash = mergeIntoHash(state);
      const fullHash = `#${newHash}`;
      if (fullHash === window.location.hash) return; // nothing changed
      lastWrittenHash.current = fullHash;
      window.location.hash = newHash; // creates a history entry
    },
    [enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleHashChange = () => {
      const current = window.location.hash;
      // Ignore changes we triggered ourselves
      if (lastWrittenHash.current === current) {
        lastWrittenHash.current = null;
        return;
      }
      const state = decodeUrlState(current);
      // Only react if there are zml params in the new hash
      if (Object.keys(state).length > 0) {
        onPopState(state);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [enabled, onPopState]);

  return { initialUrlState, pushUrlState };
}
